import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { FiHome, FiBarChart2, FiAlertCircle, FiPlus, FiSend, FiFileText, FiMessageCircle, FiArrowRight, FiTrendingUp, FiUsers, FiRadio, FiGlobe } from 'react-icons/fi';
import { IGlobalState } from '../../model/IGlobalState';

// ponytail: every figure on this page is placeholder copy from the Figma spec — no backend yet

const attention = [
    { title: 'Confirm the email for “Appendix 3Y – Change of Director\'s Interest”', sub: 'Non price-sensitive announcements wait for your approval before emailing 150 subscribers.', action: 'Review & Send' },
    { title: '2 investor questions have waited more than 48 hours', sub: 'Dev Patel asked about the June cash position; Sarah O\'Neill asked about a TSX listing.', action: 'Answer Now' },
    { title: 'Gold Mountain assay results confirm high-grade zone is still a draft', sub: 'Wallace Turner saved it yesterday. Publish it to show on the Website Hub.', action: 'Open Post' },
];

const quickActions = [
    { icon: <FiPlus />, title: 'Add an announcement', sub: 'Upload a PDF and email investors' },
    { icon: <FiSend />, title: 'Send an email campaign', sub: 'Write a news update to subscribers' },
    { icon: <FiFileText />, title: 'Run a holdings report', sub: 'See who bought or sold shares' },
    { icon: <FiMessageCircle />, title: 'Answer investor questions', sub: '5 open on the Website Hub' },
];

const glance = [
    { value: '772', label: 'shareholders', sub: '20 movements yesterday · top 20 hold 67.9%', link: 'Open Registry' },
    { value: '38', label: 'published', sub: 'Latest: Appendix 3Y, 1 Sep · 1 waiting for approval', link: 'See Announcements' },
    { value: '150', label: 'subscribed contacts', sub: 'Last campaign 27 Aug · 44.7% opened', link: 'Open Campaigns' },
    { value: '1,860', label: 'visitors this month', sub: 'Site is live · 5 open questions · 46 new sign-ups', link: 'Manage Website Hub' },
    { value: '+3.2%', label: 'registry growth', sub: 'Email opens 41.8% · Website Hub visits up 22%', link: 'View Analytics' },
    { value: 'On', label: 'auto-send for price-sensitive', sub: 'Automic connected · 4 users have access', link: 'Open Settings' },
];

const holders = [
    { rank: 1, name: 'Alpha Custody Nominees', pct: 18.4 },
    { rank: 2, name: 'Beta Global Nominees (Settlement)', pct: 9.1 },
    { rank: 2, name: 'Mr Kenneth James Parker', pct: 9.1 },
    { rank: 3, name: 'Acme Constructions Pty Ltd', pct: 6.7 },
    { rank: 4, name: 'Northstar Mining Inc', pct: 5.9 },
];

const announcements = [
    { title: 'Appendix 3Y – Change of Director\'s Interest Notice x2', when: '1 Sep, 5:29 pm', badge: 'Waiting for your approval', tone: 'warn' },
    { title: '171m @ 0.9g/t Gold Confirms Scale at Gold Mountain', when: '27 Aug, 8:43 am', sensitive: true, badge: 'Emailed to 150 · 44.7% opened', tone: 'ok' },
    { title: 'Application for quotation of securities – 49M', when: '24 Aug, 9:22 am', badge: 'Not emailed', tone: '' },
    { title: 'Release of Securities from Escrow', when: '14 Aug, 5:00 pm', badge: 'Not emailed', tone: '' },
];

const investors = [
    { text: 'Brendan Taylor asked when the next drilling update is due', meta: '2 hours ago · verified holder' },
    { text: 'Alexander Sample signed up and was matched to the registry', meta: '5 hours ago · 43,114 shares' },
    { text: 'You answered “Will there be another SPP this year?”', meta: 'Yesterday · published on the Website Hub' },
];

const stats = [['Open', 'A$0.235'], ['High', 'A$0.250'], ['Low', 'A$0.232'], ['Volume', '659K'], ['52-wk range', 'A$0.11 – 0.31'], ['Market cap', 'A$31.5M']];
const ranges = ['1D', '1W', '1M', '3M', '1Y', '5Y'];
const pricePoints = [0.215, 0.212, 0.222, 0.226, 0.231, 0.228, 0.236, 0.240, 0.238, 0.244, 0.238, 0.248, 0.260, 0.252, 0.247, 0.243, 0.240, 0.245];

const greeting = () => { const h = new Date().getHours(); return h < 12 ? 'Good Morning' : h < 17 ? 'Good Afternoon' : 'Good Evening'; };

const PriceChart: React.FC = () => {
    const w = 800, h = 160, min = 0.20, max = 0.28;
    const pts = pricePoints.map((p, i) => [i * (w / (pricePoints.length - 1)), h - ((p - min) / (max - min)) * h]);
    const line = pts.map(([x, y]) => `${x},${y}`).join(' ');
    const [lx, ly] = pts[pts.length - 1];
    return <svg className="price-chart" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none">
        <defs><linearGradient id="v2-price-fill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#00b5d1" stopOpacity="0.35" /><stop offset="100%" stopColor="#00b5d1" stopOpacity="0" />
        </linearGradient></defs>
        {[0, 0.25, 0.5, 0.75, 1].map(f => <line key={f} x1="0" x2={w} y1={f * h} y2={f * h} className="grid" />)}
        <polygon points={`0,${h} ${line} ${w},${h}`} fill="url(#v2-price-fill)" />
        <polyline points={line} className="line" />
        <line x1={lx} x2={lx} y1="0" y2={h} className="cursor" />
        <circle cx={lx} cy={ly} r="4" className="dot" />
    </svg>;
};

const Dashboard: React.FC = () => {
    const user = useSelector((state: IGlobalState) => state.user);
    const stock = useSelector((state: IGlobalState) => state.currentStock);
    const [range, setRange] = useState('1M');
    const firstName = user.username.split(/[@.]/)[0];
    const name = firstName.charAt(0).toUpperCase() + firstName.slice(1);

    return <div className="dashboard-v2">
        <div className="sub-tabs">
            <button type="button" className="active"><FiHome /> Overview</button>
            <button type="button"><FiBarChart2 /> Analytics</button>
        </div>

        <div className="page-header">
            <h1>{greeting()}, {name}</h1>
            <p>{moment().format('dddd, D MMMM YYYY')}{stock && ` · ${stock.companyName} (${stock.ticker}:${stock.exchange})`} · Registry last updated yesterday, 6:10 pm</p>
        </div>

        <div className="row row-0">
            <section className="card attention">
                <header>
                    <span className="icon-tile"><FiAlertCircle /></span>
                    <h2>Needs Your Attention</h2>
                    <span className="pill warn">{attention.length} things to do today</span>
                </header>
                {attention.map(x => <div className="item" key={x.title}>
                    <div className="text"><div className="title">{x.title}</div><div className="sub">{x.sub}</div></div>
                    <button type="button" className="btn-outline">{x.action}</button>
                </div>)}
            </section>

            <section className="card quick-actions">
                <h2>Quick Actions</h2>
                <p className="sub">The things you do most often</p>
                {quickActions.map(x => <button type="button" className="action" key={x.title}>
                    <span className="icon-tile dark">{x.icon}</span>
                    <span className="text"><span className="title">{x.title}</span><span className="sub">{x.sub}</span></span>
                    <FiArrowRight className="arrow" />
                </button>)}
            </section>
        </div>

        <section className="glance">
            <h2>Your workspace at a glance</h2>
            <div className="grid">
                {glance.map(x => <div className="stat" key={x.label}>
                    <div className="value">{x.value} <span className="label">{x.label}</span></div>
                    <div className="sub">{x.sub}</div>
                    <a href="#/" className="link">{x.link} <FiArrowRight /></a>
                </div>)}
            </div>
        </section>

        <div className="row row-2">
            <section className="card share-price">
                <header>
                    <span className="icon-tile"><FiTrendingUp /></span>
                    <h2>Share Price (ASX:49M)</h2>
                    <a href="#/" className="link">Open Share Price <FiArrowRight /></a>
                </header>
                <div className="price-row">
                    <span className="price">A$0.245</span>
                    <span className="pill ok">▲ 4.3% (+A$0.010)</span>
                    <span className="delay">today · 20 min delay</span>
                    <div className="ranges">{ranges.map(r => <button type="button" key={r} className={r === range ? 'active' : ''} onClick={() => setRange(r)}>{r}</button>)}</div>
                </div>
                <div className="chart-wrap">
                    <div className="y-axis">{['0.28', '0.26', '0.24', '0.22', '0.20'].map(v => <span key={v}>{v}</span>)}</div>
                    <PriceChart />
                    <div className="tooltip"><strong>2 Sep · A$0.245</strong><span>Vol 659K</span></div>
                </div>
                <div className="x-axis">{['5 Aug', '12 Aug', '19 Aug', '26 Aug', '2 Sep'].map(v => <span key={v}>{v}</span>)}</div>
                <div className="stats">{stats.map(([k, v]) => <div className="stat" key={k}><span className="k">{k}</span><span className="v">{v}</span></div>)}</div>
            </section>

            <section className="card top-holders">
                <header>
                    <span className="icon-tile"><FiUsers /></span>
                    <h2>Top Shareholders</h2>
                    <a href="#/" className="link">All 107 <FiArrowRight /></a>
                </header>
                {holders.map((x, i) => <div className="holder" key={i}>
                    <span className="rank">{x.rank}</span>
                    <span className="name">{x.name}</span>
                    <span className="pct">{x.pct}%</span>
                    <span className="bar"><span style={{ width: `${(x.pct / holders[0].pct) * 100}%` }} /></span>
                </div>)}
            </section>
        </div>

        <div className="row row-3">
            <section className="card announcements">
                <header>
                    <span className="icon-tile"><FiRadio /></span>
                    <h2>Recent Announcements</h2>
                    <a href="#/" className="link">All 38 <FiArrowRight /></a>
                </header>
                {announcements.map(x => <div className="item" key={x.title}>
                    <div className="text">
                        <div className="title">{x.title}</div>
                        <div className={'sub' + (x.sensitive ? ' sensitive' : '')}>{x.when}{x.sensitive && <> · Price sensitive</>}</div>
                    </div>
                    <span className={'pill ' + x.tone}>{x.badge}</span>
                </div>)}
            </section>

            <section className="card investors">
                <header>
                    <span className="icon-tile"><FiGlobe /></span>
                    <h2>Latest from Investors</h2>
                    <a href="#/" className="link">More <FiArrowRight /></a>
                </header>
                {investors.map(x => <div className="item" key={x.text}>
                    <div className="title">{x.text}</div>
                    <div className="sub">{x.meta}</div>
                </div>)}
            </section>
        </div>
    </div>;
};

export default Dashboard;
